import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
    LogService,
    Medium,
    Product,
    ShoppingCart,
    LocalWebTicket,
    AuthService,
    DateBuilder,
    Ticket,
    AppStartupService,
    CardTypes
} from '@ridango/core';

import { NgRedux } from '@angular-redux/store';

import { Observable, Subject, throwError } from 'rxjs';
import { map, tap, switchMap, catchError, takeUntil, take } from 'rxjs/operators';

import { ShoppingCartActions } from './+state/shopping-cart.actions';
import { ShoppingCartState } from './+state/shopping-cart.reducer';
import { MediumType, RequiredParameterType, TravelCardType } from '../medium/medium.model';
import {
    ActivationType,
    Cart,
    CartPaymentMethodsDetails,
    CartTicketRequest,
    PrePurchaseCheckResponse
} from '../purchase/purchase.model';
import { Product as WMBProduct } from '../purchase/purchase.model';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { ProductsService } from '../products';
import { EnhancedFareEngineTicket } from '../account-tickets-ngrx/models/ticket.model';
import { PurchaseService } from '../purchase';
import { Location } from '@angular/common';
import { CustomerAccountsFacade } from '../customer-accounts';
import { NotificationFacade } from '../notifications/+state/notification.facade';

@Injectable({
    providedIn: 'root'
})
export class ShoppingCartService {
    readonly cartIdLocalStorageKey = this.env.APP_NAME + '-cart-id';
    protected componentDestroyed$ = new Subject<void>();
    public travelCardType: TravelCardType;

    constructor(
        @Inject('ENV') private env: any,
        private ls: LogService,
        private http: HttpClient,
        private actions: ShoppingCartActions,
        private ngRedux: NgRedux<ShoppingCartState>,
        private authService: AuthService,
        private translate: TranslateService,
        private appConfig: AppStartupService,
        private productsService: ProductsService,
        private purchaseService: PurchaseService,
        private notification: NotificationFacade,
        private location: Location,
        private router: Router,
        private matDialog: MatDialog,
        private customerAccountsFacade: CustomerAccountsFacade
    ) {}

    getCartContentsById(cartId: any): Observable<ShoppingCart> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/${cartId}`;

        this.ngRedux.dispatch(this.actions.fetchCartStart());
        return this.http.get(url).pipe(
            map((data) => {
                let cart = new ShoppingCart();
                cart = Object.assign(data);
                return cart;
            })
        );
    }

    deleteServerCart(cart: ShoppingCart) {
        this.ls.log(this, 'deleteCart', cart);
        // const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/${cart.shopping_cart_id}`;
    }

    addTicketToCart(
        ticket: Ticket,
        cart: ShoppingCart = null,
        token: string,
        params: { email?: string } = {}
    ): Observable<Ticket> {
        // https://codecraft.tv/courses/angular/http/http-with-observables/
        // https://github.com/ReactiveX/rxjs/blob/master/docs_apps/movingo/src/app/content/guide/v6/migration.md
        this.ls.log(this, 'addTicketToCart', [ticket, cart]);
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add`;
        this.ngRedux.dispatch(this.actions.postTicketRequestStart());

        const payload: any = {
            product_id: ticket.product_object.product_id,
            region_id: this.env.REGION_ID,
            channel: this.env.CHANNEL,
            // in case we need to send just date (without time) then we should be still sending full date, because of included timezone
            starting: new DateBuilder(new Date(ticket.starting)).setTimeStartOfDay().getDate()
        };

        if (ticket.product_object.product_properties.filter((pp) => pp.code === 'MATRIX').length > 0) {
            payload.start_zone_id = ticket.product_price_object.origin_zone;
            payload.end_zone_id = ticket.product_price_object.destination_zone;
        }
        if (params.email) {
            payload.email = params.email;
        }
        return this.http.post(url, payload).pipe(
            map((data) => {
                let serverTicket = new Ticket();
                serverTicket = JSON.parse(JSON.stringify(ticket)); // deep copy 'quick' way

                const o = { ...serverTicket, ...data };
                serverTicket = Object.assign(o);
                return serverTicket;
            })
        );
    }

    deleteTicketFromCart(ticket: Ticket, cart: ShoppingCart): Observable<any> {
        this.ls.log(this, 'deleteTicketFromCart', [ticket, cart]);
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/${cart.shopping_cart_id}/ticket/${ticket.doc_num}`;
        return this.http.delete(url);
    }

    removeTicketFromCartState(ticket: Ticket, cart: ShoppingCart) {
        this.ls.log(this, 'removeTicketFromCartState', [ticket, cart]);
        this.ngRedux.dispatch(this.actions.removeTicketFromCart(ticket, cart));
        this.updateCartFieldsFromServer(cart);
    }

    deleteGroupedTicketFromCart(ticket: Ticket, cart: ShoppingCart): Observable<any> {
        this.ls.log(this, 'deleteGroupedTicketFromCart', [ticket, cart]);
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/${cart.shopping_cart_id}/ticket/${ticket.doc_num}`;
        return this.http.delete(url);
    }

    removeGroupedTicketFromCartState(ticket: Ticket, cart: ShoppingCart, uid: string) {
        this.ls.log(this, 'removeGroupedTicketFromCartState', [ticket, cart, uid]);
        this.ngRedux.dispatch(this.actions.removeGroupedTicketFromCart(ticket, cart, uid));
        this.updateCartFieldsFromServer(cart);
    }

    private updateCartFieldsFromServer(cart: ShoppingCart) {
        this.getCartContentsById(cart.shopping_cart_id).subscribe(
            (c) => {
                const pl = {
                    total_sum: c.total_sum,
                    total: c.total
                };
                this.ngRedux.dispatch(this.actions.updateCartTotalPrice(pl));
            },
            (err) => {
                throw new Error(err);
            }
        );
    }

    addMultipleTicketsToCart(tickets: Ticket[], cart: ShoppingCart = null): void {
        this.ls.log(this, 'addMultipleTicketsToCart', [tickets, cart]);
        // const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/${cart.shopping_cart_id}/add`;
    }

    deleteCart(): void {
        this.ngRedux.dispatch(this.actions.deleteCart());
    }

    // should deprecate, use addLocalTicketToCart instead
    addProductToCart(product: Product, card: Medium, cart: ShoppingCart = null): Observable<Ticket> {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add`;

        const payload: any = {
            product_id: product.product_id,
            region_id: this.env.REGION_ID,
            card_id: card.prefix + card.card_number,
            channel: this.env.CHANNEL,
            sms: 'N',
            card_type: card.type
        };

        if (
            product.product_type === 'W' &&
            product.product_properties &&
            product.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN') &&
            product.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX')
        ) {
            payload['price'] = product.price;
        }
        if (cart) {
            payload['shopping_cart_id'] = cart.shopping_cart_id;
        }

        return this.http.post(url, payload).pipe(
            map((ticket: Ticket) => {
                return ticket;
            })
        );
    }

    addLocalTicketToCart(
        ticket: LocalWebTicket,
        card: Medium,
        cart: ShoppingCart = null,
        channel: string = this.env.CHANNEL,
        language: string = ''
    ) {
        this.ls.log(this, 'addLocalTicketToCart', [ticket, card, cart]);
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add`;
        // card_id: card.prefix + card.card_number,
        const payload: any = {
            product_id: ticket.product_object.product_id,
            region_id: this.env.REGION_ID,
            channel: channel
        };

        // TODO: take a look at this thingy
        if (ticket.related_card_object && ticket.related_card_object.id) {
            payload['related_card_id'] = ticket.related_card_object.id;
        } else if (card.email) {
            payload['email'] = card.email;
        } else {
            payload['card_id'] = card.card_number;
            if (card.prefix) {
                payload['card_id'] = card.prefix + card.card_number;
            }

            payload['card_type'] = card.type;
        }

        if (ticket.medium_object && ticket.medium_object.type === 'S') {
            payload['card_type'] = 'S';
        }

        if (
            ticket.product_object.product_type === 'W' &&
            ticket.product_object.product_properties &&
            ticket.product_object.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN') &&
            ticket.product_object.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX')
        ) {
            payload['price'] = ticket.price;
        }

        if (ticket.product_object.product_properties.filter((pp) => pp.code === 'MATRIX').length > 0) {
            payload.start_zone_id = ticket.origin_stop_area_id;
            payload.end_zone_id = ticket.destination_stop_area_id;
        }

        if (cart) {
            payload['shopping_cart_id'] = cart.shopping_cart_id;
        }

        if (this.authService.getLoggedInUserCompanyId()) {
            payload['company_id'] = this.authService.getLoggedInUserCompanyId();
        }

        let headers = new HttpHeaders();
        if (language !== '') {
            headers = headers.set('X-app-lang', language);
        }

        return this.http.post(url, payload, { headers: headers }).pipe(
            map((t: LocalWebTicket) => {
                return t;
            })
        );
    }

    batchAddTicketsToCart(
        ticket: LocalWebTicket,
        companyId: number = null,
        cart: ShoppingCart = null,
        channel: string = this.env.CHANNEL,
        cardIds: string[] = [],
        relatedCardIds: number[] = [],
        cardType: string = '',
        emails: string[] = [],
        language: string = ''
    ) {
        this.ls.log(this, 'batchAddTicketsToCart', [ticket, cardIds, relatedCardIds, cart, channel, language]);
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add/batch`;
        // card_id: card.prefix + card.card_number,
        const payload: any = {
            product_id: ticket.product_object.product_id,
            region_id: this.env.REGION_ID,
            channel: channel,
            sms: 'N'
        };

        if (cardType) {
            payload['card_type'] = cardType;
        }

        if (cardIds.length > 0) {
            payload['card_ids'] = cardIds;
        } else if (relatedCardIds.length > 0) {
            payload['related_card_ids'] = relatedCardIds;
        } else if (emails.length > 0) {
            payload['emails'] = emails;
        } else if (ticket.travelCardsIds && ticket.travelCardsIds.length > 0) {
            payload['related_card_ids'] = ticket.travelCardsIds;
        }

        if (
            ticket.product_object.product_type === 'W' &&
            ticket.product_object.product_properties &&
            ticket.product_object.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN') &&
            ticket.product_object.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX')
        ) {
            payload['price'] = ticket.price;
        }

        if (cart) {
            payload['shopping_cart_id'] = cart.shopping_cart_id;
        }
        if (companyId) {
            payload['company_id'] = companyId;
        }
        // if ( this.authService.getLoggedInUserCompanyId() ) {
        //     payload[ 'company_id' ] = this.authService.getLoggedInUserCompanyId();
        // }

        if (ticket.product_object.product_properties.filter((pp) => pp.code === 'MATRIX').length > 0) {
            payload.start_zone_id = ticket.origin_stop_area_id;
            payload.end_zone_id = ticket.destination_stop_area_id;
        }

        let headers = new HttpHeaders();
        if (language !== '') {
            headers = headers.set('X-app-lang', language);
        }

        return this.http.post(url, payload, { headers: headers }).pipe(
            map((t: LocalWebTicket) => {
                return t;
            })
        );
    }

    postMultipleTicketPayloads(ticketPayloads: any[], cartId = null) {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add/multiple`;
        const payload = {
            shopping_cart_id: cartId,
            tickets: ticketPayloads.map((t) => {
                const { order_index: _order_index, ...ticket } = t;
                return ticket;
            })
        };
        return this.http.post(url, payload);
    }

    postSubscription(paymentTokenId: number, ticket: LocalWebTicket, locale?: string) {
        const url = `${this.env.API_BASE_URL}/everypay/subscription/create/channel/web/region/${this.env.REGION_ID}/payment_token/${paymentTokenId}`;

        const payload: any = {
            product_id: ticket.product_object.product_id,
            starting: ticket.starting
        };

        if (ticket.product_object.product_properties.filter((pp) => pp.code === 'MATRIX').length > 0) {
            payload.start_zone_id = ticket.origin_stop_area_id;
            payload.end_zone_id = ticket.destination_stop_area_id;
        }

        if (ticket.related_card_object && ticket.related_card_object.id) {
            payload['related_card_id'] = ticket.related_card_object.id;
        } else if (ticket.medium_object && ticket.medium_object.card_number) {
            payload['card_id'] = ticket.medium_object.card_number;
            if (ticket.medium_object.prefix) {
                payload['card_id'] = ticket.medium_object.prefix + ticket.medium_object.card_number;
            }
        }

        if (locale !== '') {
            const headers = new HttpHeaders().set('X-app-lang', locale);
            return this.http.post(url, payload, { headers });
        }

        return this.http.post(url, payload);
    }

    setCartStatus(status: string) {
        this.ngRedux.dispatch(this.actions.setStatus(status));
    }

    setCartAgreement(agreed: boolean) {
        this.ngRedux.dispatch(this.actions.setCartTermsAgreed(agreed));
    }

    hydrateState(cart) {
        this.ngRedux.dispatch(this.actions.hydrateCartState(cart));
    }

    /* V2 cart things, others may become obsolete soon */

    postPayloads(payloadData: any, language: string = '') {
        const url = `${this.env.API_BASE_URL}/${this.env.REGION_ID}/cart/add/multiple`;
        const tickets = payloadData.payloads;
        const payload = {
            shopping_cart_id: null,
            tickets: tickets.map((t) => {
                const { order_index: _order_index, ...ticket } = t;
                if (this.env?.FORMATTING?.USE_WHOLE_FINAL_DAY && ticket.ending && typeof ticket.ending === 'string') {
                    // Replace 'T00:00:00' with 'T23:59:59' to cover the whole final day.
                    ticket.ending = ticket.ending.replace('T00:00:00', 'T23:59:59');
                }

                if (
                    this.env.PURCHASE?.MERGE_MEDIUM_TRAVELCARDS &&
                    payloadData?.medium?.type?.startsWith(MediumType.TravelCardU.slice(0, 10))
                ) {
                    delete ticket.card_type;
                }

                return ticket;
            })
        };

        let headers = new HttpHeaders();
        if (language != '') {
            headers = headers.set('X-app-lang', language);
        }
        return this.http.post(url, payload, { headers: headers });
    }

    getCartId(): number {
        try {
            const { id, deletionTimestamp } = JSON.parse(localStorage.getItem(this.cartIdLocalStorageKey));

            if (deletionTimestamp && new Date(deletionTimestamp).valueOf() < new Date().valueOf()) {
                this.setCartId(null);
                return null;
            }

            return isNaN(parseInt(id)) ? null : parseInt(id);
        } catch (e) {
            return null;
        }
    }

    setCartId(id: number, deletionTimestamp?: Date) {
        if (!id) {
            localStorage.removeItem(this.cartIdLocalStorageKey);
            return;
        }

        localStorage.setItem(
            this.cartIdLocalStorageKey,
            JSON.stringify({
                id: id.toString(),
                deletionTimestamp: deletionTimestamp
            })
        );
    }

    private getDistributionChannel(): string {
        return this.env.IS_MOBILE_APP ? 'mobile' : 'WEB';
    }

    prePurchaseCheck(params: {
        tickets: [
            {
                productId: string;
                parametersCode?: string;
                quantity?: number;
                activationDate?: string | Date;
                dynamicProductParameters?: { [key: string]: string };
            }
        ];
    }): Observable<PrePurchaseCheckResponse> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            take(1),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/channel/${this.getDistributionChannel()}/pre-purchase-check`;
                const url = new URL(baseUrl);

                if (this.env.IS_NEW_AFC && selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.post<PrePurchaseCheckResponse>(url.toString(), params).pipe(
                    catchError((error) => {
                        this.purchaseService.setPurchaseDataLoading(false);
                        const errorMessage = error?.error?.message || error?.error?.description || error?.message;
                        this.notification.error(this.translate.instant(errorMessage));
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    addProductToCartV2(data: { shoppingCartId?: string | number; tickets: CartTicketRequest[]; activatedAt?: string }) {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            take(1),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (this.env.IS_NEW_AFC && selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                let headers = new HttpHeaders();
                headers = headers.set('Accept-Language', this.translate.currentLang);
                return this.http.post<Cart>(url.toString(), data, { headers }).pipe(
                    tap((cart) => {
                        this.setCartId(
                            cart.shoppingCartId,
                            cart.deletionTimestamp || new Date(new Date().valueOf() + 15 * 60 * 1000)
                        );
                    })
                );
            })
        );
    }

    getTravelCardPrefix(): string {
        const cardRegionProperty = this.appConfig.getPropertyByCode('WEB_ALLOWED_CARDS');
        const allowedCards = JSON.parse(cardRegionProperty?.value || '[]');
        return allowedCards?.find((card) => card.type === TravelCardType.TravelCardUTN2)?.prefix || '';
    }

    createCartCopy(cartId: number): Observable<Cart> {
        const cartCopy$ =
            this.env.IS_NEW_AFC || this.env.WMB?.WMB_PUBLIC_API
                ? this.createNewAfcCartCopy(cartId)
                : this.createLegacyCartCopy(cartId);

        return cartCopy$.pipe(catchError(() => throwError(() => new Error('Failed to create cart copy'))));
    }

    private createNewAfcCartCopy(cartId: number): Observable<Cart> {
        return this.getShoppingCartStatus(cartId).pipe(
            switchMap((cart) => {
                const tickets: CartTicketRequest[] = cart.tickets.map((ticket) => ({
                    productId: ticket.productId?.toString(),
                    startZoneId: ticket.startZoneId,
                    endZoneId: ticket.endZoneId,
                    email: ticket.email,
                    cardType: ticket.cardType,
                    cardId: ticket.cardType === CardTypes.QR_CARD ? null : ticket.cardId,
                    parametersCode: ticket.parametersCode || ticket.productParametersCode,
                    dynamicProductParameters: ticket.dynamicProductParameters,
                    starting: ticket.starting,
                    units: ticket.units,
                    price:
                        ticket.price?.amount != null
                            ? {
                                  amount: Number(ticket.price.amount),
                                  currency: ticket.price.currency
                              }
                            : undefined
                }));

                return this.addProductToCartV2({
                    shoppingCartId: null,
                    tickets
                });
            })
        );
    }

    private createLegacyCartCopy(cartId: number): Observable<Cart> {
        return this.getCartContentsById(cartId).pipe(
            switchMap((cart) => {
                const tickets: CartTicketRequest[] = cart.tickets.map((ticket) => ({
                    cardId: ticket.card_type === CardTypes.QR_CARD ? null : ticket.card_id,
                    productId: ticket.product_id.toString(),
                    startZoneId: ticket.start_zone_id,
                    endZoneId: ticket.end_zone_id,
                    email: ticket.email,
                    price: ticket.price ? { amount: ticket.price } : undefined,
                    units: ticket.units
                }));

                return this.addProductToCartV2({
                    shoppingCartId: null,
                    tickets
                });
            })
        );
    }

    getShoppingCartStatus(cartId: number) {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            take(1),
            switchMap((selectedAccount) => {
                const baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${cartId}/channel/${this.getDistributionChannel()}`;
                const url = new URL(baseUrl);

                if (this.env.IS_NEW_AFC && selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<Cart>(url.toString(), {});
            })
        );
    }

    deleteTicket(cartId, ticketId) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${cartId}/channel/${this.getDistributionChannel()}/ticket/${ticketId}`;
        return this.http.delete<Cart>(url);
    }

    corporatePayment(shoppingCartId: number, payload): Observable<any> {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${shoppingCartId}/channel/${this.getDistributionChannel()}/corporate-credit-payment`;

        return this.http.post(url, payload);
    }

    getPaymentMethods(cartId?: number): Observable<CartPaymentMethodsDetails> {
        return this.customerAccountsFacade.selectedAccount$.pipe(
            take(1),
            switchMap((selectedAccount) => {
                let baseUrl = ``;
                if (this.env.IS_NEW_AFC) {
                    baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${cartId}/channel/${this.env.CHANNEL}/payment/methods`;
                } else {
                    baseUrl = `${this.env.WMB.WMB_PUBLIC_API}/v1/payment/methods`;
                }

                const url = new URL(baseUrl);

                if (selectedAccount && selectedAccount.type.code === 'CORPORATE') {
                    url.searchParams.set('customerAccountId', selectedAccount.id.toString());
                }

                return this.http.get<CartPaymentMethodsDetails>(url.toString());
            })
        );
    }

    deleteSavedCard(cardId) {
        const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/payment/methods/card/${cardId}`;

        return this.http.delete(url);
    }

    redeemValueCode(cartId: number, code: string) {
        let url;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${cartId}/channel/${this.getDistributionChannel()}/discount`;
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/cart/${cartId}/value-codes`;
        }
        return this.http.put<Cart>(url, this.env.IS_NEW_AFC ? { valueCode: code } : [code]);
    }

    deleteValueCode(cartId: number, code: string) {
        let url;
        if (this.env.IS_NEW_AFC) {
            url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${cartId}/channel/${this.getDistributionChannel()}/discount`;
            return this.http.delete<Cart>(url);
        } else {
            url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/cart/${cartId}/remove-value-codes`;
            return this.http.put<Cart>(url, [code]);
        }
    }

    zeroPricePayment(shoppingCartId?: number): Observable<any> {
        if (this.env.IS_NEW_AFC) {
            const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/cart/${shoppingCartId}/no-cost`;
            return this.http.post<Cart>(url, {});
        } else {
            const url = `${this.env.CORE_API_URL}/customer/v1/region/${this.env.REGION_ID}/cart/${shoppingCartId}/no-cost`;
            return this.http.post<Cart>(url, {});
        }
    }

    startToBuyTicket(
        deviceUserExist: string | boolean,
        routeParams: string[],
        modalComponent: ComponentType<unknown>,
        ticket?: EnhancedFareEngineTicket,
        email?: string
    ): void {
        const isAuthenticated = this.authService.getLoginStatus();
        const getConvertedDynamicParams = (params?: Record<string, string>) =>
            Object.fromEntries(Object.entries(params ?? {}).map(([k, v]) => [k, Number(v)]));
        const getTicketDataForCartCreating = (ticket: EnhancedFareEngineTicket) => {
            return {
                endZoneId: ticket.endZoneId || null,
                startZoneId: ticket.startZoneId || null,
                productId: ticket.productId.toString(),
                email: email,
                parametersCode: ticket.productParametersCode,
                dynamicProductParameters: getConvertedDynamicParams(ticket.dynamicProductParameters)
            };
        };
        const processNavigateToShoppingCart = async () => {
            if (this.env.IS_NEW_AFC) {
                if (!ticket?.productId) {
                    await this.router.navigate([this.translate.currentLang, ...routeParams]);
                    return;
                }
                this.productsService
                    .fetchProductById(ticket.productId)
                    .pipe(take(1), takeUntil(this.componentDestroyed$))
                    .subscribe(async (product: WMBProduct) => {
                        const activationMethods =
                            (product.productParameters?.availableParameters?.activations?.map(
                                (method) => method.activationTrigger
                            ) as ActivationType[]) || [];
                        const isActivationRequired =
                            product.productParameters?.requiredParameters?.includes(RequiredParameterType.ACTIVATION) ||
                            activationMethods?.length >= 2;

                        if (isActivationRequired) {
                            const { typeOfProduct, geography, timeIntervals, productId, dynamicProductParameters } =
                                ticket as EnhancedFareEngineTicket as any;

                            const productCategory =
                                typeOfProduct.code === 'single' ? this.env.PRODUCT_CATEGORY_AREA_NAME : 'area';
                            const zone = geography.zones[0].code;
                            const productType =
                                typeOfProduct.code === 'single'
                                    ? 'productType:single'
                                    : `timeInterval:${timeIntervals[0].code}`;

                            const productSegments: string[] = [];
                            const hasDynamicParams =
                                dynamicProductParameters && Object.keys(dynamicProductParameters).length > 0;

                            if (hasDynamicParams) {
                                Object.entries(dynamicProductParameters).forEach(([key, value]) => {
                                    productSegments.push(`${productId}:${key}`, String(value));
                                });
                            } else {
                                productSegments.push(`${productId}`, '1');
                            }

                            const ticketUrlSegments = [
                                this.translate.currentLang,
                                'purchase-v2',
                                'areas',
                                `productCategory:${productCategory}`,
                                'product-area',
                                `zone:${zone}`,
                                'category-product',
                                `${productType}`,
                                'product',
                                ...productSegments,
                                'ticket-activation',
                                'AFTER_PURCHASE'
                            ];

                            this.router.navigate(ticketUrlSegments);

                            this.purchaseService.emitActivationUrl(
                                this.location.path().replace(ActivationType.AFTER_PURCHASE, 'AFTER_PURCHASE')
                            );
                        } else {
                            const cartTicket = getTicketDataForCartCreating(ticket);
                            this.addProductToCartV2({ shoppingCartId: null, tickets: [cartTicket] })
                                .pipe(takeUntil(this.componentDestroyed$))
                                .subscribe(async () => {
                                    await this.router.navigate([this.translate.currentLang, ...routeParams]);
                                    this.componentDestroyed$.next();
                                    this.componentDestroyed$.complete();
                                });
                        }
                    });
                return;
            }

            if (ticket) {
                const cartTicket: CartTicketRequest = {
                    endZoneId: ticket.endZoneId || null,
                    startZoneId: ticket.startZoneId || null,
                    productId: ticket.productId.toString(),
                    email: null
                };

                this.addProductToCartV2({ shoppingCartId: null, tickets: [cartTicket] })
                    .pipe(take(1), takeUntil(this.componentDestroyed$))
                    .subscribe(async () => {
                        await this.router.navigate([this.translate.currentLang, ...routeParams]);
                        this.componentDestroyed$.next();
                        this.componentDestroyed$.complete();
                    });
            } else {
                await this.router.navigate([this.translate.currentLang, ...routeParams]);
            }
        };

        if (isAuthenticated || (!this.env.PURCHASE_FLOW.SHOW_ANONYMOUS_USER_MODAL_ALWAYS && deviceUserExist)) {
            processNavigateToShoppingCart();
        } else if (this.env.DEVICE_CUSTOMER_ENABLED && Capacitor.isNativePlatform()) {
            this.matDialog.open(modalComponent, {
                width: '90%',
                maxWidth: '80vh',
                panelClass: 'anonymous-purchase-modal',
                autoFocus: true,
                data: { ticket, routeParams }
            });
        } else {
            this.router.navigate([this.translate.currentLang, 'login']);
        }
    }

    //This is just to have some control over the minimum length of the "other" trip purpose text
    get getValidOtherPurposeLength(): number {
        return this.env.PURCHASE_FLOW?.MINIMUM_OTHER_PURPOSE_LENGTH
            ? this.env.PURCHASE_FLOW?.MINIMUM_OTHER_PURPOSE_LENGTH
            : 5;
    }
}
